<template>
  <div class="wrapper" ref="tableWrapper">
    <div
      v-if="isShowOperationModal"
      class="alert-operation-modal"
      :style="alertOperationModalStyle"
      v-click-outside="closeAlertOperationModal"
    >
      <p @click="editAlert(selectedAlert.id)">Edit</p>
      <p @click="setAlertSnoozeStatus(selectedAlert)">
        {{ getSnoozeOperationName(selectedAlert) }}
      </p>
      <p @click="deleteAlert(selectedAlert.id)">Delete</p>
    </div>

    <vxe-table
      ref="xTable"
      empty-text="No data"
      border="inner"
      :align="allAlign"
      :row-config="rowConfig"
      :expand-config="{
        iconOpen: 'fa fa-chevron-up',
        iconClose: 'fa fa-chevron-down',
        toggleMethod: handleToggleExpand
      }"
      :cell-class-name="cellClassName"
      :data="alertListOfSelectedAccountFilteredByAlertNameQuery"
      class="alert-table"
    >
      <vxe-column type="expand" width="48">
        <template #content="{ row }">
          <div class="expand-wrapper">
            <AlertRSDetailContainer
              :alertDataObj="row"
            ></AlertRSDetailContainer>
          </div>
        </template>
      </vxe-column>
      <vxe-column
        field="name"
        title="Alert name"
        sortable
        sort-by="name"
        show-overflow="ellipsis"
        min-width="192px"
      ></vxe-column>
      <vxe-column
        type="html"
        field="status"
        title="Status"
        sortable
        sort-by="status"
        min-width="160px"
        :filters="[
          { label: 'Clean', value: 'Clean' },
          { label: 'Fired', value: 'Fired' },
          { label: 'Firing', value: 'Firing' },
          { label: 'Snoozed', value: 'Snoozed' }
        ]"
        :filter-multiple="false"
        :formatter="formatStatus"
      ></vxe-column>
      <vxe-column
        field="dateOfLastTrigger"
        title="Timestamp (last trigger)"
        sortable
        sort-by="dateOfLastTrigger"
        min-width="240px"
        :formatter="formatDate"
      ></vxe-column>
      <vxe-column title="" width="64px">
        <template #default="{ row, rowIndex }">
          <div class="operation">
            <div
              class="icon-wrapper"
              @click="setOperationModal(row, rowIndex, $event)"
            >
              <fa icon="ellipsis-v" class="icon"></fa>
            </div>
          </div>
        </template>
      </vxe-column>
    </vxe-table>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import dayjs from 'dayjs';
import * as vClickOutside from 'v-click-outside';

import AlertRSDetailContainer from './alertTableParts/AlertRSDetailContainer.vue';
import { STATUS } from '../../../constants/alertsConstant';
import Spinner from '../../ui/Spinner.vue';
import { formatDateAsLocalTimestamp } from '../../../utils/common';

import {
  getElementPositionTop,
  getOmnibarHeight,
  getSubheaderHeight
} from '../../../utils/BOM';

export default {
  name: 'AlertRSTable',
  components: {
    AlertRSDetailContainer,
    Spinner
  },
  directives: {
    clickOutside: vClickOutside.directive
  },

  data() {
    return {
      allAlign: 'left', // 'left', 'right' or 'center'
      rowConfig: { isHover: false },
      selectedAlertIndex: null,
      selectedAlert: {},
      isShowOperationModal: false,
      modalTop: 0
    };
  },
  computed: {
    ...mapGetters(['isMobile']),
    ...mapState('alertsRoutingStatusStore', [
      'alertNameQuery',
      'expandedAlertId',
      'isLoadingData'
    ]),
    ...mapGetters('alertsRoutingStatusStore', ['alertsListOfSelectedAccount']),

    alertListOfSelectedAccountFilteredByAlertNameQuery() {
      const query = new RegExp(this.alertNameQuery, 'i');

      return this.alertsListOfSelectedAccount.filter(
        alert => alert.name.search(query) > -1
      );
    },
    alertOperationModalStyle() {
      if (this.selectedAlertIndex === null) return {};

      return {
        top: `${this.modalTop}px`,
        right: '3.5rem'
      };
    }
  },
  watch: {
    // '$store.state.alertsRoutingStatusStore.expandedAlertId'
    expandedAlertId: {
      immediate: false,
      handler(val) {
        if (val !== null && val !== undefined) {
          // Clear filter conditions. Otherwise, the alert may not exist in current table
          this.$refs.xTable.clearFilter();

          // Find the alertIndex of the last firing alert.
          const alertIndex = this.alertListOfSelectedAccountFilteredByAlertNameQuery.findIndex(
            item => item.id === val
          );

          // Check if the last firing alert exists in alert list.
          if (alertIndex !== null && alertIndex !== undefined) {
            // Fold all rows first. Otherwise, the tableRowElementList cannot be calculated correctly.
            this.$refs.xTable.clearRowExpand();

            // Use setTimeout to allow all rows to be folded first.
            setTimeout(() => {
              const tableRowElementList = document.querySelectorAll(
                '.vxe-body--row'
              );

              // Expand the last firing alert.
              this.$refs.xTable.setRowExpand(
                this.alertListOfSelectedAccountFilteredByAlertNameQuery[
                  alertIndex
                ],
                true
              );

              if (!tableRowElementList || !tableRowElementList[alertIndex])
                return;

              // Use recursive function to check whether the alert history loading is done. Scroll to the specific alert only after loading history data.
              const scrollAfterLoadingData = () => {
                // Without setTimeout, scroll behavior cannot be triggered properly in Firefox.
                setTimeout(() => {
                  if (!this.isLoadingData) {
                    // Get the top position of the last firing alert.
                    const alertOffsetTop = getElementPositionTop(
                      tableRowElementList[alertIndex]
                    );

                    // Scroll to the last firing alert.
                    const omnibarHeight = getOmnibarHeight();
                    const subheaderHeight = getSubheaderHeight();
                    window.scrollTo({
                      top: alertOffsetTop - omnibarHeight - subheaderHeight,
                      left: 0,
                      behavior: 'smooth'
                    });

                    // Reset expandedAlertId in state in case users click on "More details" again.
                    this.setExpandedAlertId(null);
                  } else {
                    scrollAfterLoadingData();
                  }
                }, 200);
              };

              scrollAfterLoadingData();
            }, 0);
          }
        }
      }
    }
  },
  methods: {
    ...mapActions('alertsRoutingStatusStore', ['fetchHistoryOfSelectedAlert']),
    ...mapMutations('alertsRoutingStatusStore', ['setExpandedAlertId']),

    handleToggleExpand({ row }) {
      this.fetchHistoryOfSelectedAlert(row.id);
      return true;
    },
    cellClassName({ row, rowIndex, column, columnIndex }) {
      if (column.property === 'status') {
        let addedClassName = '';
        switch (row.status) {
          case STATUS.FIRING:
            addedClassName = 'prefix-icon red-dot';
            break;
          case STATUS.FIRED:
            addedClassName = 'prefix-icon red-circle';
            break;
          case STATUS.SNOOZED:
            addedClassName = 'prefix-icon yellow-dot';
            break;
          case STATUS.CLEAN:
            addedClassName = 'prefix-icon green-dot';
            break;
          default:
            addedClassName = '';
            break;
        }
        return addedClassName;
      }
    },
    formatDate({ cellValue }) {
      return formatDateAsLocalTimestamp(cellValue);
    },
    formatStatus({ row: alert }) {
      if (alert.status !== STATUS.SNOOZED) {
        return `<span class="status">${alert.status}</span>`;
      } else {
        const snoozeToDate = alert.snoozeTo
          ? dayjs(alert.snoozeTo).format('DD MMM YYYY')
          : '';
        return `<span class="status">${
          alert.status
        }</span><span class="snooze"> (until ${snoozeToDate})</span>`;
      }
    },
    handleClick(val) {
      alert(val);
    },
    setOperationModal(val, rowIndex, event) {
      // Calculate the modal top position (the distance from modal top to the Table top in y-axis) according to table top position and mouse click event position
      const tableTop = this.$refs.tableWrapper.getBoundingClientRect().top;
      const mouseY = event.y;
      this.modalTop = mouseY - tableTop;

      this.selectedAlertIndex = rowIndex;
      this.selectedAlert = val;
      this.isShowOperationModal = true;
    },
    closeAlertOperationModal() {
      this.isShowOperationModal = false;
      this.selectedAlert = {};
      this.selectedAlertIndex = null;
    },
    editAlert(val) {
      this.$emit('onEditAlert', val);
      this.closeAlertOperationModal();
    },
    setAlertSnoozeStatus(val) {
      if (val.status !== STATUS.SNOOZED) {
        this.$emit('onSnoozeAlert', val.id);
      } else {
        this.$emit('onUnSnoozeAlert', val.id);
      }
      this.closeAlertOperationModal();
    },
    deleteAlert(val) {
      this.$emit('onDeleteAlert', val);
      this.closeAlertOperationModal();
    },
    getSnoozeOperationName(row) {
      return row.status === STATUS.SNOOZED ? 'Un-Snooze' : 'Snooze';
    }
  }
};
</script>

<style lang="scss" scoped>
.wrapper {
  position: relative;
  padding-bottom: 6rem;
}
.expand-wrapper {
  padding: 1rem 3rem 0 3rem;
}

.alert-operation-modal {
  position: absolute;
  z-index: 99;
  width: 10rem;
  border: 1px #e6e6e6 solid;
  background-color: #fff;
  box-shadow: $alert-card-shadow;

  p {
    margin: 0;
    padding: 0.5rem 1.5rem;
    cursor: pointer;
  }
  p:nth-child(1) {
    padding-top: 1rem;
  }
  p:nth-child(4) {
    padding-bottom: 1rem;
    color: $warning-red;
  }
  p:hover {
    background-color: rgba($color: #666, $alpha: 0.1);
  }
}

.alert-table {
  ::v-deep .vxe-header--row {
    background-color: #e6e6e6;
  }

  ::v-deep .vxe-table--filter-wrapper {
    text-align: left;
  }

  ::v-deep .status {
    padding-left: 0.5rem;
  }

  ::v-deep .snooze {
    font-size: 0.8rem;
    font-style: italic;

    @media screen and (max-width: 1120px) and (min-width: 993px) {
      padding-left: 0.5rem;
      display: block;
    }
  }

  ::v-deep .vxe-cell--title {
    .info {
      margin-left: 0.25rem;
      font-size: 1rem;
      color: $apnic-blue;
    }
  }
}

.operation {
  position: relative;

  .icon-wrapper {
    padding-right: 1rem;
    white-space: initial;
    cursor: pointer;

    .icon {
      display: inline-block;
      width: 100%;
    }
  }
}
</style>
